import {
  SLUG_PRODUCT_IDENTIFIER_KEY,
  SEARCH_PRODUCT_IDENTIFIER_KEY,
} from "@constants/products/listing";
import { WAREHOUSE_PRODUCT } from "@constants/products/detail";

export const getExternalIdsFromSlugProducts = (products, getExternalId) =>
  products.flatMap((product) => product.variantsIncludingMaster.map(getExternalId).filter(Boolean));

export const getExternalIdsFromSearchListingProducts = (products, getExternalId) =>
  products.flatMap((product) => product.variants.map(getExternalId).filter(Boolean));

export const getProductExternalIds = (products) => {
  if (!products?.length) {
    return [];
  }
  const getExternalId = ({ externalId, isMaster }, _index, currentArray) =>
    currentArray.length > 1 ? !isMaster && externalId : externalId;

  if (SLUG_PRODUCT_IDENTIFIER_KEY in products[0]) {
    return getExternalIdsFromSlugProducts(products, getExternalId);
  }

  return getExternalIdsFromSearchListingProducts(products, getExternalId);
};

export const getVariantsQuantity = ({ inventoryData, products, defaultSizeId, defaultColorId }) => {
  const generateVariantInventoryDetailMapping = (variants, product, sizeId, colorId) => {
    if (product.trackInventory || product.type === WAREHOUSE_PRODUCT) {
      return variants.reduce(
        (mapping, { externalId, optionValues }) => {
          const variantInventory = inventoryData?.[externalId] || {
            in_stock_quantity: 0,
            in_transit_quantity: 0,
            deficit_quantity: 0,
            display_oos_label: false,
          };
          const {
            in_stock_quantity: inLockerRawQuantity,
            in_transit_quantity: inTransitRawQuantity,
            deficit_quantity: deficitRawQuantity,
            display_oos_label: displayOosLabel,
          } = variantInventory;

          const inLocker = inLockerRawQuantity ?? 0;
          const inTransit = inTransitRawQuantity ?? 0;
          const deficit = deficitRawQuantity ?? 0;

          mapping.total.inLocker += inLocker;
          mapping.total.inTransit += inTransit;
          mapping.total.deficit += deficit;
          mapping.total.displayOosLabel ||= displayOosLabel;
          mapping.total.available += inLocker + inTransit - deficit;

          if (SLUG_PRODUCT_IDENTIFIER_KEY in product) {
            const sizeOptionValue = optionValues.filter((value) => value.optionTypeId === sizeId);
            const colorOptionValue = optionValues.filter((value) => value.optionTypeId === colorId);

            if (sizeOptionValue.length) {
              mapping[sizeOptionValue[0].id] = (mapping[sizeOptionValue[0].id] || 0) + inLocker;
            }
            if (colorOptionValue.length) {
              mapping[colorOptionValue[0].id] = (mapping[colorOptionValue[0].id] || 0) + inLocker;
            }
          }
          return mapping;
        },
        { total: { inLocker: 0, inTransit: 0, deficit: 0, available: 0, displayOosLabel: false } },
      );
    } else {
      return {
        total: { inLocker: 0, inTransit: 0, deficit: 0, available: 0, displayOosLabel: false },
      };
    }
  };

  return products.reduce((currentVariantQtyMapping, product) => {
    if (SLUG_PRODUCT_IDENTIFIER_KEY in product) {
      const variants = product.variantsIncludingMaster;

      currentVariantQtyMapping[product.id] = generateVariantInventoryDetailMapping(
        variants,
        product,
        defaultSizeId,
        defaultColorId,
      );
    }
    if (SEARCH_PRODUCT_IDENTIFIER_KEY in product) {
      currentVariantQtyMapping[product.id] = generateVariantInventoryDetailMapping(
        product.variants,
        product,
      );
    }
    return currentVariantQtyMapping;
  }, {});
};

export const getUpdatedLocalCartQuantity = ({
  quantity,
  isGuestOrPreviewMode,
  productId,
  storeId,
  isEditMode,
  lineItems,
}) => {
  let updatedQty = quantity;
  if (isGuestOrPreviewMode && !isEditMode && lineItems?.length > 0) {
    const newLineItems = [...lineItems];
    const existingProductIndex = newLineItems.findIndex(
      (item) => item.productId === productId && item.storeId === storeId,
    );
    const existingProduct = newLineItems[existingProductIndex];
    updatedQty = (existingProduct?.quantity ?? 0) + (quantity ?? 0);
  }
  return updatedQty;
};
